<template>
  <div v-if="hasAccess(MARKETING_MATERIALS_ACCESS, MARKETING_MATERIALS_ACCESS_MANAGE)"
       class="marketing-materials monthlyPaymentCalculator" ref="monthlyPaymentCalculator"
  >
    <h1>Monthly Payment Calculator</h1>
    <template v-if="pages && pages.length === 0">
      <notification-financing-page/>
    </template>
    <template v-else-if="pages && calculatorCode">
      <GetCodeWidget
          :active="codeModalActive"
          :code="code"
          :email-sending="emailSending"
          :nameWidjet="calculator"
          :nameWidjetFull="calculatorFull"
          @submit="submit"
          @close="closeModal"/>

      <PageSelector />

      <div class="columns" style="margin-bottom: 0.75rem">
        <div class="column"
             :class="[windowWidth > 1023 ? 'is-one-third':'is-half']">
          <p>
            Use this calculator to show your clients what their monthly payments can be.
            Then help your clients to get their personalized offers.
            You can seamlessly integrate this calculator into your webpage
            by changing its color to suite your website design or to make it stand out.
          </p>
          <div class="choose">
            <h3 class="title">Choose widget color:</h3>
            <div class="chooseOptions">
              <button v-for="(option, key) in options" :key="key"
                      v-bind:class="{ active: selected === key }"
                      @click="optionClickHandler(key)">
                <span :style="{ background: getGradient(option.color1, option.color2) }"></span>
              </button>
            </div>
            <div class="chooseSwitch">
              <div class="chooseSwitchText">
                Without the border:
              </div>
              <b-switch v-model="withoutBorder"
                        size="is-small"
                        class="enh-switch"
                        type="is-green"
                        @change.native="changeWithoutBorder">
              </b-switch>
            </div>
            <div class="chooseSwitch">
              <div class="chooseSwitchText">
                Show 'See Your Personalized Offers' button
                <b-tooltip label="You can choose to show or hide
                              the ‘See Your Personal Offers’ button. This button is
                              switched on by default."
                           class="block-tooltip is-gray"
                           type="is-info"
                           size="is-small"
                           :delay="200"
                           :position="getTooltipPosition(570, 'is-top')"
                           animated
                           multilined>
                  <b-icon pack="fa" size="is-small" icon="question-circle">
                  </b-icon>
                </b-tooltip>
              </div>
              <b-switch v-model="hideLink"
                        size="is-small"
                        class="enh-switch"
                        type="is-green"
                        @change.native="changeHideLink">
              </b-switch>
            </div>
            <b-button
                class="getCalculator"
                type="is-success"
                @click.prevent="showCalculatorWidgetModal"
                :loading="codeLoading"
            >
              Get Calculator Code
            </b-button>
          </div>
        </div>
        <div class="column" ref="widgetContainer"  id="w-container" v-html="calculatorCode"></div>
      </div>
    </template>
  </div>
</template>

<script>
import {
  mapActions,
  mapGetters, mapMutations,
  mapState,
} from 'vuex';
import * as CONTRACTOR_ACTIONS from '@/store/modules/contractor/actions';
import * as MUTATIONS from '@/store/mutations';
import NotificationFinancingPage from '@/components/NotificationFinancingPage.vue';
import GetCodeWidget from '@/components/Modal/GetCodeWidget.vue';
import {
  paymentCalculatorWidgetCode,
  paymentCalculatorSendToEmail,
} from '@/api/widget';
import PageSelector from '@/views/MarketingMaterials/components/PageSelector.vue';

export default {
  name: 'MonthlyPaymentCalculator',
  components: {
    PageSelector,
    GetCodeWidget,
    NotificationFinancingPage,
  },
  data: () => ({
    pageReferralUrl: null,
    siteUrl: null,
    options: {
      default: {
        color1: '#68BA62',
        color2: '#1C418C',
        coBrandedColor: '#FFFFFF',
      },
      'red/blue': {
        color1: '#B31417',
        color2: '#064C8F',
        coBrandedColor: '#FFFFFF',
      },
      'orange/blue': {
        color1: '#FF531B',
        color2: '#054078',
        coBrandedColor: '#FFFFFF',
      },
      'gold/black': {
        color1: '#BD921E',
        color2: '#3A3A37',
        coBrandedColor: '#FFFFFF',
      },
      'yellow/purple': {
        color1: '#FFD200',
        color2: '#472152',
        coBrandedColor: '#293033',
      },
    },
    selected: 'default',
    border: true,
    withoutBorder: false,
    hideLink: true,
    codeLoading: false,
    tipModalActive: false,
    codeModalActive: false,
    emailSending: false,
    code: null,
    calculatorCode: null,
    calculator: 'Calculator',
    calculatorFull: 'Monthly Payment Calculator',
    calculatorScript: '',
    MARKETING_MATERIALS_ACCESS: { resource: 'marketing-materials', code: 'access' },
    MARKETING_MATERIALS_ACCESS_MANAGE: { resource: 'marketing-materials', code: 'manage_access' },
  }),
  computed: {
    ...mapState('contractor', { contractorId: (state) => state.id }),
    ...mapState('user', { userId: (state) => state.id }),
    ...mapGetters(['hasAccess', 'sidebarWidth']),
    ...mapGetters('contractor', ['selectedPage', 'pages']),
    windowWidth() {
      return this.$store.state.windowWidth;
    },
  },
  watch: {
    windowWidth() {
      // if you abruptly switch from a large resolution to a small one,
      // then payment calculator width won't be correct.
      setTimeout(() => {
        const event = new Event('DOMContentLoaded');
        document.dispatchEvent(event);
      }, 200);
    },
    sidebarWidth() {
      setTimeout(() => {
        const event = new Event('DOMContentLoaded');
        document.dispatchEvent(event);
      }, 200);
    },
  },
  async activated() {
    if (!this.contractorId) return;
    this.startLoading();

    try {
      if (!this.selectedPage?.id) {
        await this.updPages(this.contractorId);
      }
      await this.optionClickHandler();
      const event = new Event('DOMContentLoaded');
      document.dispatchEvent(event);
      window.scrollTo(0, 0);
    } catch (error) {
      this.$buefy.snackbar.open({
        message: 'An unexpected error occurred, please contact support@enhancify.com',
        actionText: ' ',
        position: 'is-top',
        indefinite: true,
        type: 'is-danger',
      });
    }

    this.finishLoading();
  },
  methods: {
    ...mapActions('contractor', { updPages: CONTRACTOR_ACTIONS.LOAD_PAGES }),
    ...mapActions('contractor', { setSelectedPage: CONTRACTOR_ACTIONS.SET_SELECTED_PAGE }),
    ...mapMutations({
      startLoading: MUTATIONS.START_LOADING,
      finishLoading: MUTATIONS.FINISH_LOADING,
    }),

    getTooltipPosition(width, position) {
      const widthWithoutSidebar = this.windowWidth - this.sidebarWidth;
      if (widthWithoutSidebar >= width) {
        return 'is-right';
      }
      return position;
    },
    changeWithoutBorder() {
      this.optionClickHandler();
    },
    changeHideLink() {
      this.optionClickHandler();
    },
    getGradient(color1, color2) {
      return `linear-gradient(90deg, ${color1}, ${color1} 50%, ${color2} 50%)`;
    },
    async optionClickHandler(key) {
      if (key) {
        this.selected = key;
      }
      if (this.contractorId === null || !this.selectedPage?.id) return;
      const params = {
        userId: this.userId,
        pageId: this.selectedPage.id,
        color1: this.options[this.selected].color1.replace('#', '$'),
        color2: this.options[this.selected].color2.replace('#', '$'),
        coBrandedColor: this.options[this.selected].coBrandedColor.replace('#', '$'),
        border: !this.withoutBorder,
        hideLink: !this.hideLink ? 1 : 0,
      };
      const { data } = await paymentCalculatorWidgetCode(params);
      this.calculatorCode = data.html;
      this.calculatorScript = data.script;
      const calculator = document.createElement('script');
      calculator.classList.add('calculator-script');
      this.$nextTick(() => {
        if (document.getElementById('w-container')) {
          const calculatorScript = document.getElementById('w-container').getElementsByClassName('calculator-script');
          if (!calculatorScript.length) {
            calculator.setAttribute('src', this.calculatorScript);
            document.getElementById('w-container').appendChild(calculator);
          }
        }
      });
      // eslint-disable-next-line func-names
      calculator.onload = function () {
        const event = new Event('DOMContentLoaded');
        document.dispatchEvent(event);
      };
    },
    closeModal() {
      this.code = null;
      this.tipModalActive = false;
      this.codeModalActive = false;
    },
    openCodeModal() {
      this.codeModalActive = true;
    },
    showCalculatorWidgetModal() {
      if (this.contractorId === null || !this.selectedPage?.id) return;

      const params = {
        userId: this.userId,
        pageId: this.selectedPage.id,
        color1: this.options[this.selected].color1.replace('#', '$'),
        color2: this.options[this.selected].color2.replace('#', '$'),
        coBrandedColor: this.options[this.selected].coBrandedColor.replace('#', '$'),
        border: !this.withoutBorder,
        hideLink: !this.hideLink ? 1 : 0,
      };
      paymentCalculatorWidgetCode(params).then((response) => {
        this.calculatorCode = response.data.html;
        this.calculatorScript = response.data.script;
        const calculator = document.createElement('script');
        calculator.setAttribute('src', this.calculatorScript);
        this.code = calculator.outerHTML + this.calculatorCode;
        this.openCodeModal();
      })
        .catch(() => this.$buefy.toast.open({ message: 'An unexpected error occurred, please contact support@enhancify.com', type: 'is-danger', duration: 3000 }))
        .finally(() => {
        });
    },
    submit(email) {
      if (this.contractorId === null || !this.selectedPage?.id) return;
      this.emailSending = true;
      const formData = new FormData();
      formData.set('userId', this.userId);
      formData.set('pageId', this.selectedPage.id);
      formData.set('color1', this.options[this.selected].color1.replace('#', '$'));
      formData.set('color2', this.options[this.selected].color2.replace('#', '$'));
      formData.set('coBrandedColor', this.options[this.selected].coBrandedColor.replace('#', '$'));
      formData.set('border', !this.withoutBorder);
      formData.set('hideLink', !this.hideLink ? 1 : 0);
      formData.set('email', email);
      paymentCalculatorSendToEmail(formData)
        .then((response) => {
          if (response.status === 200) {
            this.$buefy.toast.open({ message: 'Success!', type: 'is-success', duration: 3000 });
          }
        })
        .catch(() => this.$buefy.toast.open({ message: 'An unexpected error occurred, please contact support@enhancify.com', type: 'is-danger', duration: 3000 }))
        .finally(() => {
          this.emailSending = false;
        });
    },
  },
};
</script>

<style lang="stylus" scoped>
.monthlyPaymentCalculator
  .choose.block-title
    margin-bottom 0.5rem
  .choose
    .title
      font-weight 400
      font-size 16px
      line-height 24px
      margin-top 30px
      margin-bottom 0
      color #969ea2
    .chooseOptions
      button
        background none
        width 54px
        height 54px
        border 1px solid rgba(143, 167, 179, 0.298039)
        border-radius 3px
        padding 10px
        cursor pointer
        margin-top: 13.5px
        &:not(:last-child)
          margin-right 13.5px
        &.active,
        &:hover
          border-color #68BA62
        span
          display block
          width 100%
          height 100%
          border-radius 100%
    .chooseSwitch
      display flex
      margin-top: 30px
      .chooseSwitchText
        display inline-flex
        align-items: center
        margin-right: 20px
        color #969ea2
      >>>.switch.enh-switch
        span.check
          &:before
            box-shadow none
    .getCalculator
      font-size 19px
      height 48px
      margin-top 35px
      text-transform uppercase
      @media screen and (max-width 499px)
        width 100%
</style>
